import {
  Component,
  signal,
  computed,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserHelper } from '@app/core/helpers/user.helper';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { UserService } from '@app/core/services/user.service';
import { ConfirmationService } from 'primeng/api';
import { NavService } from '@app/core/services/nav.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ServerSideFileHelper } from '@app/core/helpers/server-side-file.helper';
import { PermissionService } from '@app/core/services/permission.service';
import { PermissionEnum } from '@app/generated/graphql';
import { NotificationsOrchestratorService } from '../notifications-v2/notifications-orchestrator.service';
import { AlertsOrchestratorService } from '../alerts-menu/alerts-orchestrator.service';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Avatar } from 'primeng/avatar';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Popover } from 'primeng/popover';
import { HasPermissionDirective } from '../../../../core/directives/has-permission.directive';
import { NotificationsV2Component } from '../notifications-v2/notifications-v2.component';
import { AlertsMenuComponent } from '../alerts-menu/alerts-menu.component';
import { PackhouseConnectivityComponent } from '../packhouse-connectivity/packhouse-connectivity.component';
import { NotificationsComponent } from '../notifications/notifications.component';
import { SupportMenuComponent } from '../support-menu/support-menu.component';

@Component({
  selector: 'app-profile-menu',
  templateUrl: './profile-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Bind,
    Button,
    Avatar,
    AngularSvgIconModule,
    Popover,
    HasPermissionDirective,
    NotificationsV2Component,
    AlertsMenuComponent,
    PackhouseConnectivityComponent,
    NotificationsComponent,
    SupportMenuComponent,
  ],
})
export class ProfileMenuComponent {
  userService = inject(UserService);
  private confirmationService = inject(ConfirmationService);
  private authenticationService = inject(AuthenticationService);
  navService = inject(NavService);
  layoutService = inject(LayoutService);
  permissionService = inject(PermissionService);
  notificationsOrchestratorService = inject(NotificationsOrchestratorService);
  alertsOrchestratorService = inject(AlertsOrchestratorService);

  PermissionEnum = PermissionEnum;
  profilePopoverOpen: boolean = false;

  readonly user = toSignal(this.userService.user$, {
    initialValue: this.userService.getUserValue(),
  });
  readonly company = toSignal(this.userService.company$, {
    initialValue: this.userService.getCompanyValue(),
  });
  readonly userFeedUnseenItemCount = signal<number>(0);
  readonly alertsCount = signal<number>(0);
  readonly notificationCount = signal<number>(0);

  readonly showActionItemBadge = computed(
    () =>
      this.userFeedUnseenItemCount() > 0 ||
      this.alertsCount() > 0 ||
      this.notificationCount() > 0,
  );

  readonly userFullName = computed(() =>
    this.user() ? UserHelper.getFullName(this.user()) : '',
  );
  readonly userAvatarUrl = computed(() =>
    this.user()?.avatarFile
      ? ServerSideFileHelper.fullUrl(this.user()?.avatarFile)
      : null,
  );
  readonly hasUserAvatar = computed(() => !!this.user()?.avatarFile?.url);

  constructor() {}

  onProfilePopoverShow(): void {
    this.profilePopoverOpen = true;
  }

  onProfilePopoverHide(): void {
    this.profilePopoverOpen = false;
  }

  onLogout(event: Event): void {
    this.confirmationService.confirm({
      target: event.target as HTMLElement,
      header: 'Logout?',
      closable: true,
      closeOnEscape: true,
      message: 'Are you sure you want to logout of the AgrigateOne platform?',
      acceptButtonProps: {
        label: 'Logout',
        severity: 'danger',
      },
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'contrast',
        variant: 'text',
      },
      accept: () => this.authenticationService.logout(),
    });
  }

  onUserFeedUnseenItemCountChange(count: number): void {
    this.userFeedUnseenItemCount.set(count || 0);
  }

  onAlertsCountChange(count: number): void {
    this.alertsCount.set(count || 0);
  }

  onNotificationCountChange(count: number): void {
    this.notificationCount.set(count || 0);
  }
}
